import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useEvaluations, useProjectEvaluations, usePagination } from '../lib/hooks'
import { Button } from '../components/ui/Button'
import { Input, Select } from '../components/ui/Input'
import { StatusBadge, PriorityBadge } from '../components/ui/Badge'
import { LoadingSpinner, LoadingCard } from '../components/ui/LoadingSpinner'
import { 
  Search, 
  Filter, 
  Eye, 
  FileText, 
  TrendingUp, 
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock
} from 'lucide-react'
import { formatDate, formatRelativeTime } from '../lib/utils'

export const EvaluationsPage: React.FC = () => {
  const pagination = usePagination()
  const [searchQuery, setSearchQuery] = useState('')
  const [resultFilter, setResultFilter] = useState('')
  const [projectFilter, setProjectFilter] = useState('')

  // API hooks
  const { data: evaluationsData, isLoading } = useEvaluations({
    skip: pagination.skip,
    limit: pagination.limit,
  })

  // Update pagination when data changes
  React.useEffect(() => {
    if (evaluationsData) {
      pagination.setTotal(evaluationsData.total_count)
    }
  }, [evaluationsData, pagination.setTotal])

  const resultOptions = [
    { value: '', label: 'All Results' },
    { value: 'meets', label: 'Meets Requirements' },
    { value: 'partially_meets', label: 'Partially' },
    { value: 'not_met', label: 'Not Met' },
  ]

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'meets':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'partially_meets':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />
      case 'not_met':
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  const getConfidenceColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evaluations</h1>
          <p className="text-gray-600">Review response evaluations and results</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Search evaluations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
            
            <Select
              placeholder="Filter by result"
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
              options={resultOptions}
            />
            
            <Select
              placeholder="Filter by project"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              options={[
                { value: '', label: 'All Projects' },
                // This would be populated from a projects API call
              ]}
            />
            
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('')
                setResultFilter('')
                setProjectFilter('')
              }}
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Evaluations List */}
      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <LoadingCard key={i} />
          ))
        ) : evaluationsData?.data?.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No evaluations found</h3>
            <p className="text-gray-500">
              {searchQuery || resultFilter || projectFilter
                ? 'No evaluations match your current filters.'
                : 'No evaluations have been completed yet.'}
            </p>
          </div>
        ) : (
          evaluationsData?.data?.map((evaluation) => (
            <div key={evaluation.id} className="card">
              <div className="card-content">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getResultIcon(evaluation.result)}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {evaluation.vendor_response.vendor_name}
                      </h3>
                      <StatusBadge status={evaluation.result} />
                      {evaluation.is_ai_generated && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          AI Generated
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Project:</strong> {evaluation.vendor_response.project.title}
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Requirement:</strong> {evaluation.requirement_id}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Evaluator:</strong> {evaluation.evaluator.full_name}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Confidence Score:</strong> 
                          <span className={`ml-1 font-medium ${getConfidenceColor(evaluation.confidence_score)}`}>
                            {evaluation.confidence_score}%
                          </span>
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Source:</strong> Page {evaluation.source_page}
                          {evaluation.source_section && `, Section ${evaluation.source_section}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Evaluated:</strong> {formatRelativeTime(evaluation.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-md">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Evidence</h4>
                        <p className="text-sm text-gray-600">{evaluation.evidence}</p>
                      </div>
                      
                      <div className="p-3 bg-blue-50 rounded-md">
                        <h4 className="text-sm font-medium text-blue-700 mb-1">Reasoning</h4>
                        <p className="text-sm text-blue-600">{evaluation.reasoning}</p>
                      </div>
                      
                      {evaluation.extracted_details && (
                        <div className="p-3 bg-green-50 rounded-md">
                          <h4 className="text-sm font-medium text-green-700 mb-1">Extracted Details</h4>
                          <pre className="text-sm text-green-600 whitespace-pre-wrap">
                            {JSON.stringify(evaluation.extracted_details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Link to={`/evaluations/${evaluation.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {evaluationsData && evaluationsData.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, evaluationsData.total_count)} of {evaluationsData.total_count} evaluations
          </p>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.prevPage}
              disabled={!pagination.hasPrevPage}
            >
              Previous
            </Button>
            
            <span className="text-sm text-gray-700">
              Page {pagination.page + 1} of {evaluationsData.total_pages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.nextPage}
              disabled={!pagination.hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default EvaluationsPage